import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Event, Participant, Fight, Bet, LeaderboardEntry } from '../types';
import { calculateLeaderboard } from '../utils/leaderboard';

/**
 * Event State Context
 * Manages the current event, participants, fights, and bets
 */

interface EventState {
  event: Event | null;
  participants: Participant[];
  fights: Fight[];
  bets: Bet[];
  leaderboard: LeaderboardEntry[];
}

interface EventContextValue extends EventState {
  // Event management
  setEvent: (event: Event | null) => void;
  createEvent: (name: string, adminId: string) => Event;
  
  // Participant management
  addParticipant: (participant: Participant) => void;
  updateParticipant: (participantId: string, updates: Partial<Participant>) => void;
  getParticipant: (participantId: string) => Participant | undefined;
  
  // Fight management
  addFight: (fight: Fight) => void;
  updateFight: (fightId: string, updates: Partial<Fight>) => void;
  getFight: (fightId: string) => Fight | undefined;
  
  // Bet management
  addBet: (bet: Bet) => void;
  updateBet: (betId: string, updates: Partial<Bet>) => void;
  getBetsByParticipant: (participantId: string) => Bet[];
  getBetsByFight: (fightId: string) => Bet[];
  
  // Leaderboard
  updateLeaderboard: () => void;
  
  // Event actions
  loadEvent: (event: Event) => void;
  updateEvent: (event: Event) => void;
  clearEvent: () => void;
}

const EventContext = createContext<EventContextValue | null>(null);

interface EventProviderProps {
  children: ReactNode;
}

export function EventProvider({ children }: EventProviderProps) {
  const [state, setState] = useState<EventState>({
    event: null,
    participants: [],
    fights: [],
    bets: [],
    leaderboard: [],
  });

  // Event management
  const setEvent = useCallback((event: Event | null) => {
    setState((prev) => {
      const participants = event?.participants ?? [];
      const bets = participants.flatMap((p) => p.bets);
      const leaderboard = event ? calculateLeaderboard(participants) : [];
      
      // Fights should be in chronological order (first fight at index 0)
      // Reverse the array if needed to ensure proper display order
      const fights = event?.fights ?? [];
      
      return {
        ...prev,
        event,
        participants,
        fights,
        bets,
        leaderboard,
      };
    });
  }, []);

  const createEvent = useCallback((name: string, adminId: string): Event => {
    const eventId = `event-${Date.now()}`;
    const shareableLink = `${window.location.origin}/join/${eventId}`;
    
    const newEvent: Event = {
      id: eventId,
      name,
      createdAt: Date.now(),
      status: 'upcoming',
      finished: false,
      participants: [],
      fights: [],
      shareableLink,
      adminId,
      isPublic: false,
      published: false, // Events start unpublished (draft)
    };
    
    setEvent(newEvent);
    return newEvent;
  }, [setEvent]);

  // Participant management
  const addParticipant = useCallback((participant: Participant) => {
    setState((prev) => {
      if (!prev.event) return prev;
      
      const updatedParticipants = [...prev.participants, participant];
      const updatedEvent: Event = {
        ...prev.event,
        participants: updatedParticipants,
      };
      
      return {
        ...prev,
        event: updatedEvent,
        participants: updatedParticipants,
      };
    });
    
    updateLeaderboard();
  }, []);

  const updateParticipant = useCallback((participantId: string, updates: Partial<Participant>) => {
    setState((prev) => {
      if (!prev.event) return prev;
      
      const updatedParticipants = prev.participants.map((p) =>
        p.id === participantId ? { ...p, ...updates } : p
      );
      
      const updatedEvent: Event = {
        ...prev.event,
        participants: updatedParticipants,
      };
      
      return {
        ...prev,
        event: updatedEvent,
        participants: updatedParticipants,
      };
    });
    
    updateLeaderboard();
  }, []);

  const getParticipant = useCallback((participantId: string) => {
    return state.participants.find((p) => p.id === participantId);
  }, [state.participants]);

  // Fight management
  const addFight = useCallback((fight: Fight) => {
    setState((prev) => {
      if (!prev.event) return prev;
      
      const updatedFights = [...prev.fights, fight];
      const updatedEvent: Event = {
        ...prev.event,
        fights: updatedFights,
      };
      
      return {
        ...prev,
        event: updatedEvent,
        fights: updatedFights,
      };
    });
  }, []);

  const updateFight = useCallback((fightId: string, updates: Partial<Fight>) => {
    setState((prev) => {
      if (!prev.event) return prev;
      
      const updatedFights = prev.fights.map((f) =>
        f.id === fightId ? { ...f, ...updates } : f
      );
      
      const updatedEvent: Event = {
        ...prev.event,
        fights: updatedFights,
      };
      
      return {
        ...prev,
        event: updatedEvent,
        fights: updatedFights,
      };
    });
  }, []);

  const getFight = useCallback((fightId: string) => {
    return state.fights.find((f) => f.id === fightId);
  }, [state.fights]);

  // Bet management
  const addBet = useCallback((bet: Bet) => {
    setState((prev) => {
      if (!prev.event) return prev;
      
      const updatedBets = [...prev.bets, bet];
      
      // Update participant's bets array
      const updatedParticipants = prev.participants.map((p) =>
        p.id === bet.participantId
          ? { ...p, bets: [...p.bets, bet] }
          : p
      );
      
      const updatedEvent: Event = {
        ...prev.event,
        participants: updatedParticipants,
      };
      
      return {
        ...prev,
        event: updatedEvent,
        participants: updatedParticipants,
        bets: updatedBets,
      };
    });
  }, []);

  const updateBet = useCallback((betId: string, updates: Partial<Bet>) => {
    setState((prev) => {
      if (!prev.event) return prev;
      
      const updatedBets = prev.bets.map((b) =>
        b.id === betId ? { ...b, ...updates } : b
      );
      
      // Update participant's bets array
      const updatedParticipants = prev.participants.map((p) => ({
        ...p,
        bets: p.bets.map((b) => (b.id === betId ? { ...b, ...updates } : b)),
      }));
      
      const updatedEvent: Event = {
        ...prev.event,
        participants: updatedParticipants,
      };
      
      return {
        ...prev,
        event: updatedEvent,
        participants: updatedParticipants,
        bets: updatedBets,
      };
    });
    
    updateLeaderboard();
  }, []);

  const getBetsByParticipant = useCallback((participantId: string) => {
    return state.bets.filter((b) => b.participantId === participantId);
  }, [state.bets]);

  const getBetsByFight = useCallback((fightId: string) => {
    return state.bets.filter((b) => b.fightId === fightId);
  }, [state.bets]);

  // Leaderboard
  const updateLeaderboard = useCallback(() => {
    setState((prev) => {
      const leaderboard = calculateLeaderboard(prev.participants);
      return {
        ...prev,
        leaderboard,
      };
    });
  }, []);

  // Event actions
  const loadEvent = useCallback((event: Event) => {
    setEvent(event);
  }, [setEvent]);

  const updateEvent = useCallback((event: Event) => {
    setEvent(event);
  }, [setEvent]);

  const clearEvent = useCallback(() => {
    setState({
      event: null,
      participants: [],
      fights: [],
      bets: [],
      leaderboard: [],
    });
  }, []);

  const value: EventContextValue = {
    ...state,
    setEvent,
    createEvent,
    addParticipant,
    updateParticipant,
    getParticipant,
    addFight,
    updateFight,
    getFight,
    addBet,
    updateBet,
    getBetsByParticipant,
    getBetsByFight,
    updateLeaderboard,
    loadEvent,
    updateEvent,
    clearEvent,
  };

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
}

export function useEvent(): EventContextValue {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEvent must be used within EventProvider');
  }
  return context;
}

